import {
  BodyText,
  Button,
  Code,
  Heading,
  Input,
  Layout,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
  Textarea,
  toast,
} from '@postscript/components';
import * as IconSet from '@postscript/components/dist/esm/icons';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import MainLayout from '../components/Layout/MainLayout';
import { StyledBlockHeading } from '../components/Layout/SimpleStyledComponents';
import SystemBlock from '../components/Layout/SystemBlock';
import registryData from './sandbox/pages-registry.json';

const TableContainer = styled.div`
  width: 100%;
`;

const StyledTable = styled(Table)`
  width: 100%;
`;

const StyledHeaderButton = styled(Button)`
  position: absolute;
  right: 0;
  top: 108px;
`;

const CodeBlock = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  padding: var(--spacing-3);
  background: var(--gray-1);
  border-radius: var(--border-radius-medium);
`;

interface Project {
  name: string;
  title: string;
  description: string;
  author: string;
  route: string;
  fileName: string;
  createdAt: string;
}

interface Registry {
  pages: Project[];
}

const Sandbox = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalStep, setModalStep] = useState<'form' | 'script'>('form');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    author: '',
  });
  const [generatedScript, setGeneratedScript] = useState('');
  const [nameError, setNameError] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const history = useHistory();

  // Get projects from imported registry
  const registry = registryData as Registry;
  const projects = registry.pages;

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setModalStep('form');
    setFormData({ name: '', description: '', author: '' });
    setNameError('');
    setCopySuccess(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalStep('form');
    setFormData({ name: '', description: '', author: '' });
    setNameError('');
    setCopySuccess(false);
    setGeneratedScript('');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear name error when user types
    if (name === 'name' && nameError) {
      setNameError('');
    }
  };

  const validateAndGenerateScript = () => {
    // Validate required fields
    if (!formData.name.trim()) {
      setNameError('Name is required');
      return;
    }

    // Check if name already exists
    const nameExists = projects.some(
      (p) => p.name.toLowerCase() === formData.name.toLowerCase(),
    );

    if (nameExists) {
      setNameError('A page with this name already exists');
      return;
    }

    // Generate script with single quotes for safer pasting
    const script = `npm run create-page '${formData.name}' '${formData.description}' '${formData.author}'`;
    setGeneratedScript(script);
    setModalStep('script');
  };

  const handleCopyScript = () => {
    navigator.clipboard.writeText(generatedScript);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleCopyDeleteScript = (project: Project) => {
    const deleteScript = `npm run delete-page '${project.name}'`;
    navigator.clipboard.writeText(deleteScript);
    toast.success(
      <>
        Copied to clipboard:
        <br />
        <code>{deleteScript}</code>
      </>,
    );
  };

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.author.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const tableItems = filteredProjects.map((project) => ({
    nameColumn: {
      text: project.title || project.name,
      description: project.description,
      to: project.route,
    },
    dataColumns: [
      <BodyText key="author" size="medium">
        {project.author}
      </BodyText>,
    ],
    actionColumn: (
      <Layout gap="var(--spacing-1)">
        <Button
          size="small"
          variant="delete"
          onClick={() => handleCopyDeleteScript(project)}
        >
          Delete
        </Button>
        <Button
          size="small"
          variant="secondary"
          onClick={() => history.push(project.route)}
        >
          View Project
        </Button>
      </Layout>
    ),
  }));

  return (
    <MainLayout>
      <Helmet>
        <title>Sandbox - Page Directory</title>
        <meta
          name="description"
          content="Directory of all pages in the design sandbox"
        />
      </Helmet>
      <SystemBlock id="sandbox-list" size="tight">
        <StyledBlockHeading style={{ paddingTop: '108px' }}>
          <Heading as="h2" size="small">
            Projects
          </Heading>
          <BodyText size="large">
            Browse all available projects in the design sandbox
          </BodyText>
          <StyledHeaderButton onClick={handleOpenModal}>
            New Project
          </StyledHeaderButton>
        </StyledBlockHeading>

        <TableContainer>
          <StyledTable
            id="projects-table"
            cardWrapper
            size="medium"
            items={tableItems}
            isEmpty={filteredProjects.length === 0}
            emptyState={{
              heading: 'No projects found',
              description:
                'Try adjusting your search query or create a new project using the CLI',
            }}
            search={{
              placeholder: 'Search items',
              icon: IconSet.Search,
              value: searchQuery,
              onChange: (e) => setSearchQuery(e.target.value),
            }}
            globalColumnSettings={{
              name: {
                heading: 'Name',
              },
              dataColumns: [
                {
                  heading: 'Author',
                  minWidth: 150,
                },
              ],
              action: {
                heading: 'Actions',
                align: 'right',
                minWidth: 96,
              },
            }}
          />
        </TableContainer>
      </SystemBlock>

      <Modal isOpen={isModalOpen} onCancel={handleCloseModal}>
        <ModalHeader onCancel={handleCloseModal}>
          {modalStep === 'form' ? 'New Project' : 'Copy Command'}
        </ModalHeader>
        <ModalBody>
          {modalStep === 'form' ? (
            <Layout gap="var(--spacing-3)" vertical align="justify">
              <div>
                <Input
                  label="Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="My Awesome Page"
                  fieldSize="medium"
                  fieldWidth="full"
                  error={!!nameError}
                />
                {nameError && (
                  <BodyText size="small" color="var(--error-color)">
                    {nameError}
                  </BodyText>
                )}
              </div>
              <Textarea
                label="Description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="What is this page for?"
                fieldSize="medium"
                fieldWidth="full"
                rows={4}
              />
              <Input
                label="Author"
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="Your name"
                fieldSize="medium"
                fieldWidth="full"
              />
            </Layout>
          ) : (
            <Layout gap="var(--spacing-3)" vertical align="justify">
              <BodyText size="medium">
                Copy and paste this command into your terminal to create the
                page:
              </BodyText>
              <CodeBlock>
                <Code>{generatedScript}</Code>
                <Button
                  variant="plain"
                  size="small"
                  onClick={handleCopyScript}
                  icon={copySuccess ? IconSet.Check : IconSet.Copy}
                >
                  {copySuccess ? 'Copied!' : 'Copy'}
                </Button>
              </CodeBlock>
              <BodyText size="small" color="var(--gray-6)">
                After running the command, the page will automatically appear in
                the list above and be available at its generated route.
              </BodyText>
            </Layout>
          )}
        </ModalBody>
        <ModalFooter>
          {modalStep === 'form' ? (
            <>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" onClick={validateAndGenerateScript}>
                Generate Page Script
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              icon={IconSet.Refresh}
              onClick={() => window.location.reload()}
            >
              Refresh Page
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </MainLayout>
  );
};

export default Sandbox;
